import React from 'react';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import PageLayout from '@/components/layout/PageLayout';
import PageHeader from '@/components/layout/PageHeader';
import { Shield, Key, Lock, ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { demoAdminLogin, isDemoAdmin } from '@/utils/demoAdmin';
import { useDemoStore } from '@/store/demoStore';
import { toast } from 'react-hot-toast';

const AdminAccess: React.FC = () => {
  const navigate = useNavigate();
  const { isDemoMode } = useDemoStore();
  const isAdmin = isDemoAdmin();
  
  const handleEnableAdmin = () => {
    if (!isDemoMode) {
      toast.error('Admin access is only available in demo mode');
      return;
    }
    
    demoAdminLogin();
  };
  
  const handleGoToAdmin = () => {
    navigate('/admin');
  };
  
  return (
    <PageLayout>
      <PageHeader 
        title="Admin Access" 
        description="Access the platform administration panel"
      />
      
      <div className="max-w-md mx-auto">
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.5 }}
        >
          <Card className="backdrop-blur-sm bg-dark-800/80 border-dark-700">
            <CardHeader>
              <CardTitle className="flex items-center">
                <Shield className="mr-2 h-6 w-6 text-primary" />
                Admin Panel Access
              </CardTitle>
              <CardDescription>
                {isDemoMode 
                  ? "Enable admin access in demo mode to explore the admin panel"
                  : "Admin access is restricted to authorized personnel only"}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="p-4 bg-dark-700 rounded-lg border border-dark-600">
                  <h3 className="font-medium mb-2 flex items-center">
                    <Key className="h-4 w-4 mr-2 text-primary-500" />
                    Admin Status:
                  </h3>
                  <div className="flex items-center mt-2">
                    <div className={`w-3 h-3 rounded-full mr-2 ${isAdmin ? 'bg-green-500' : 'bg-red-500'}`}></div>
                    <span>{isAdmin ? 'Admin access enabled' : 'Admin access disabled'}</span>
                  </div>
                </div>
                
                {isDemoMode && !isAdmin && (
                  <motion.div 
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="p-4 bg-primary-500/10 text-primary-400 rounded-lg border border-primary-500/20"
                  >
                    <h3 className="font-medium mb-1 flex items-center">
                      <Lock className="h-4 w-4 mr-2" />
                      Demo Mode Available
                    </h3>
                    <p className="text-sm">
                      In demo mode, you can enable admin access to explore all platform features.
                    </p>
                  </motion.div>
                )}
                
                {!isDemoMode && (
                  <motion.div 
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="p-4 bg-yellow-500/10 text-yellow-500 rounded-lg border border-yellow-500/20"
                  >
                    <h3 className="font-medium mb-1 flex items-center">
                      <Lock className="h-4 w-4 mr-2" />
                      Restricted Access
                    </h3>
                    <p className="text-sm">
                      Admin access is only available to authorized personnel. Please contact the platform administrator if you need access.
                    </p>
                  </motion.div>
                )}
              </div>
            </CardContent>
            <CardFooter className="flex flex-col space-y-2">
              {isDemoMode && !isAdmin && (
                <Button 
                  className="w-full" 
                  onClick={handleEnableAdmin}
                >
                  <Shield className="mr-2 h-4 w-4" />
                  Enable Admin Access
                </Button>
              )}
              
              {isAdmin && (
                <Button 
                  className="w-full" 
                  onClick={handleGoToAdmin}
                >
                  <ArrowRight className="mr-2 h-4 w-4" />
                  Go to Admin Panel
                </Button>
              )}
              
              <Button 
                variant="outline" 
                className="w-full mt-2" 
                onClick={() => navigate('/')}
              >
                Return to Home
              </Button>
            </CardFooter>
          </Card>
        </motion.div>
      </div>
    </PageLayout>
  );
};

export default AdminAccess;